import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  PanResponder,
  Animated,
  Easing,
} from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ParentStackParamList } from "../../navigation/types";
import { Mascot } from "../../components/Mascot";
import { colors, fontFamily, radii } from "../../theme";

type Props = NativeStackScreenProps<ParentStackParamList, "Welcome">;

type SlideArt = "show" | "controls" | "stats";
type Slide = { title: string; subtitle: string; art: SlideArt };

const SLIDES: Slide[] = [
  {
    title: "Cartoons that teach",
    subtitle: "Short videos paired with quick questions, made for ages 2–7.",
    art: "show",
  },
  {
    title: "You set the rules",
    subtitle:
      "Daily limits, bedtime lock, allowed categories — all from your phone.",
    art: "controls",
  },
  {
    title: "See what they learned",
    subtitle:
      "A weekly recap shows minutes watched, quiz wins, and topics mastered.",
    art: "stats",
  },
];

// ─── Idle-animated art components ─────────────────────────────────────────
// Each scene runs its own continuous loop(s) so the welcome screen feels
// alive after the entrance settles (the previous version sat dead, which
// the user described as "freezy"). Loops cleanup on unmount.

function ArtShow() {
  // Slow rotating halo of three coloured dots orbiting the mascot.
  const spin = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(spin, {
        toValue: 1,
        duration: 6200,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [spin]);
  const rotate = spin.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "360deg"],
  });
  return (
    <View style={artStyles.center}>
      <Animated.View
        pointerEvents="none"
        style={[artStyles.haloRing, { transform: [{ rotate }] }]}
      >
        <View
          style={[
            artStyles.haloDot,
            { top: -6, left: 110, backgroundColor: "#FF8A4C" },
          ]}
        />
        <View
          style={[
            artStyles.haloDot,
            { top: 100, right: -6, backgroundColor: colors.success },
          ]}
        />
        <View
          style={[
            artStyles.haloDot,
            { bottom: 18, left: 24, backgroundColor: "#5B8DEF" },
          ]}
        />
      </Animated.View>
      <Mascot mood="cheer" color={colors.primary} size={180} bob sparkles />
    </View>
  );
}

function ArtControls() {
  // Sequential pulse: the 4 tiles "light up" one at a time (1.0 → 1.12 → 1.0)
  // with a staggered delay so it reads as the parent flipping switches.
  // Single shared Animated.Value with an interpolated keyframe per tile.
  const wave = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(wave, {
        toValue: 1,
        duration: 2200,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [wave]);
  // Each tile fires inside a narrow window centered at its slot (0..3).
  const pulse = (slot: number) =>
    wave.interpolate({
      // before / start / peak / end / after — window width = 0.2
      inputRange: [
        Math.max(0, slot / 4 - 0.001),
        slot / 4,
        slot / 4 + 0.1,
        slot / 4 + 0.2,
        Math.min(1, slot / 4 + 0.201),
      ],
      outputRange: [1, 1, 1.16, 1, 1],
      extrapolate: "clamp",
    });
  // Bubble mascot drifts gently up/down.
  const drift = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(drift, {
          toValue: 1,
          duration: 1400,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(drift, {
          toValue: 0,
          duration: 1400,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [drift]);
  const bubbleY = drift.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -6],
  });
  return (
    <View style={artStyles.controlsWrap}>
      <View style={artStyles.controlsCard}>
        {[colors.primary, colors.bgWarm, colors.bgWarm, colors.success].map(
          (bg, i) => (
            <Animated.View
              key={i}
              style={[
                artStyles.tile,
                { backgroundColor: bg, transform: [{ scale: pulse(i) }] },
              ]}
            />
          ),
        )}
      </View>
      <Animated.View
        style={[artStyles.controlsBubble, { transform: [{ translateY: bubbleY }] }]}
      >
        <Mascot mood="happy" color={colors.primary} size={56} />
      </Animated.View>
    </View>
  );
}

function ArtStats() {
  // 7 weekday bars; index 5 highlighted.
  const heights = [24, 38, 18, 52, 30, 60, 42];
  const FINAL = heights.map((h) => h + 28);
  // Bars grow from 0 → final height on entrance, staggered. `height` isn't
  // a native-driver property — but it's a one-shot ~700ms animation, not a
  // loop, so a JS-driven tween is smooth enough and avoids a wrapper hack.
  const grows = useRef(FINAL.map(() => new Animated.Value(0))).current;
  // Highlighted bar continues to gently breathe after it lands. NOTE: the
  // active bar animates `height` (a JS-only layout prop, useNativeDriver:false)
  // AND this scaleY pulse on the SAME node — so the pulse MUST also be
  // JS-driven. Mixing a native-driven transform with a JS-driven layout prop
  // on one node crashes RN ("animated node moved to native"). That was the
  // last-slide crash.
  const pulse = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const grow = Animated.stagger(
      55,
      grows.map((v, i) =>
        Animated.timing(v, {
          toValue: FINAL[i],
          duration: 520,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: false,
        }),
      ),
    );
    const breathe = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 900,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: false,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 900,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: false,
        }),
      ]),
    );
    grow.start(() => breathe.start());
    return () => {
      grow.stop();
      breathe.stop();
    };
    // Mount-only: grows/pulse are stable refs; FINAL is captured once (the
    // values are identical every render). Don't list FINAL or it re-runs each
    // render and restarts the grow-in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pulseScale = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.08],
  });
  return (
    <View style={artStyles.statsCard}>
      <View style={artStyles.barsRow}>
        {heights.map((_, i) => {
          const isActive = i === 5;
          return (
            <Animated.View
              key={i}
              style={[
                artStyles.bar,
                {
                  height: grows[i],
                  backgroundColor: isActive ? colors.primary : colors.bgWarm,
                  // Pulse only the highlighted bar. transformOrigin defaults
                  // to centre — fine here since we're at the bottom of the
                  // row and the scale is small.
                  transform: isActive ? [{ scaleY: pulseScale }] : undefined,
                },
              ]}
            />
          );
        })}
      </View>
      <Text style={artStyles.statsEyebrow}>THIS WEEK</Text>
      <Text style={artStyles.statsValue}>3h 24m</Text>
    </View>
  );
}

export default function WelcomeScreen({ navigation }: Props) {
  const [step, setStep] = useState(0);
  const slide = SLIDES[step];

  // Entrance / slide-change animation: the art and copy fade + slide + scale
  // in each time the slide changes (and on first mount). Native-driver so it
  // stays smooth on device.
  const artAnim = useRef(new Animated.Value(0)).current;
  const copyAnim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    artAnim.setValue(0);
    copyAnim.setValue(0);
    Animated.parallel([
      Animated.timing(artAnim, {
        toValue: 1,
        duration: 440,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(copyAnim, {
        toValue: 1,
        duration: 440,
        delay: 90,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, [step, artAnim, copyAnim]);

  const artAnimStyle = {
    opacity: artAnim,
    transform: [
      { translateY: artAnim.interpolate({ inputRange: [0, 1], outputRange: [26, 0] }) },
      { scale: artAnim.interpolate({ inputRange: [0, 1], outputRange: [0.92, 1] }) },
    ],
  };
  const copyAnimStyle = {
    opacity: copyAnim,
    transform: [
      { translateY: copyAnim.interpolate({ inputRange: [0, 1], outputRange: [18, 0] }) },
    ],
  };

  // Swipe left/right to move between slides (in addition to the Next button).
  // A ref feeds the responder the latest step so it isn't recreated each render;
  // onMoveShouldSet only fires on a real horizontal drag, so button taps still
  // pass through.
  const stepRef = useRef(step);
  stepRef.current = step;
  const pan = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, g) =>
        Math.abs(g.dx) > 16 && Math.abs(g.dx) > Math.abs(g.dy) * 1.4,
      onPanResponderRelease: (_, g) => {
        const s = stepRef.current;
        if (g.dx <= -40 && s < SLIDES.length - 1) setStep(s + 1);
        else if (g.dx >= 40 && s > 0) setStep(s - 1);
      },
    }),
  ).current;

  const handleNext = () => {
    if (step < SLIDES.length - 1) setStep(step + 1);
    else navigation.replace("Signup");
  };
  const handleSkip = () => navigation.replace("Signup");

  return (
    <View style={styles.container} {...pan.panHandlers}>
      <TouchableOpacity onPress={handleSkip} style={styles.skipBtn}>
        <Text style={styles.skipText}>Skip</Text>
      </TouchableOpacity>

      <View style={styles.artHolder}>
        <Animated.View style={artAnimStyle}>
          {slide.art === "show" && <ArtShow />}
          {slide.art === "controls" && <ArtControls />}
          {slide.art === "stats" && <ArtStats />}
        </Animated.View>
      </View>

      <Animated.View style={[styles.copy, copyAnimStyle]}>
        <Text style={styles.title}>{slide.title}</Text>
        <Text style={styles.subtitle}>{slide.subtitle}</Text>
      </Animated.View>

      <View style={styles.footer}>
        <View style={styles.dots}>
          {SLIDES.map((_, i) => (
            <View
              key={i}
              style={[
                styles.dot,
                i === step ? styles.dotActive : null,
              ]}
            />
          ))}
        </View>

        <TouchableOpacity style={styles.nextBtn} onPress={handleNext} activeOpacity={0.85}>
          <Text style={styles.nextText}>
            {step < SLIDES.length - 1 ? "Next" : "Get started"}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bgWarm,
    paddingHorizontal: 24,
    paddingTop: 56,
    paddingBottom: 32,
  },
  skipBtn: {
    alignSelf: "flex-end",
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  skipText: {
    fontFamily,
    fontSize: 13,
    fontWeight: "800",
    color: colors.ink2,
  },
  artHolder: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  copy: {
    alignItems: "center",
    paddingHorizontal: 24,
    marginBottom: 24,
  },
  title: {
    fontFamily,
    fontSize: 30,
    fontWeight: "900",
    color: colors.ink,
    textAlign: "center",
    letterSpacing: -0.3,
    marginBottom: 10,
  },
  subtitle: {
    fontFamily,
    fontSize: 16,
    fontWeight: "600",
    color: colors.ink2,
    textAlign: "center",
    lineHeight: 22,
    maxWidth: 280,
  },
  footer: {
    alignItems: "center",
  },
  dots: {
    flexDirection: "row",
    gap: 6,
    marginBottom: 20,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: "rgba(46,35,22,0.20)",
  },
  dotActive: {
    width: 22,
    backgroundColor: colors.primary,
  },
  nextBtn: {
    width: "100%",
    backgroundColor: colors.primary,
    borderRadius: radii.md,
    paddingVertical: 16,
    alignItems: "center",
    // baked-in inset bottom highlight (matches `inset 0 -3px 0 rgba(0,0,0,0.12)`)
    borderBottomWidth: 3,
    borderBottomColor: "rgba(0,0,0,0.12)",
  },
  nextText: {
    fontFamily,
    color: "#fff",
    fontSize: 16,
    fontWeight: "800",
    letterSpacing: 0.2,
  },
});

const artStyles = StyleSheet.create({
  center: {
    alignItems: "center",
    justifyContent: "center",
  },
  // Slide 1: rotating halo of colored dots around the mascot.
  haloRing: {
    position: "absolute",
    width: 220,
    height: 220,
    alignSelf: "center",
  },
  haloDot: {
    position: "absolute",
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  // Slide 2: 4-tile rounded card with mascot bubble peeking from corner
  controlsWrap: {
    width: 200,
    height: 200,
    alignItems: "center",
    justifyContent: "center",
  },
  controlsCard: {
    width: 160,
    height: 160,
    borderRadius: 32,
    backgroundColor: colors.surface,
    padding: 14,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.10,
    shadowRadius: 28,
    elevation: 6,
  },
  tile: {
    // Fixed 62×62 (card 160 − 28 padding = 132 inner; 62 + 8 gap + 62 = 132).
    // `width: "47%"` + `aspectRatio: 1` collapsed to zero height on Android,
    // leaving the card blank — explicit dimensions render the four tiles.
    width: 62,
    height: 62,
    borderRadius: 12,
  },
  controlsBubble: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: colors.surface,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.10,
    shadowRadius: 14,
    elevation: 4,
  },
  // Slide 3: white card w/ bar chart and weekly minutes
  statsCard: {
    width: 220,
    padding: 14,
    backgroundColor: colors.surface,
    borderRadius: 22,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.10,
    shadowRadius: 28,
    elevation: 6,
  },
  barsRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 5,
    height: 100,
    marginBottom: 10,
  },
  bar: {
    flex: 1,
    borderRadius: 4,
  },
  statsEyebrow: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    color: colors.ink2,
    letterSpacing: 1.4,
    marginBottom: 2,
  },
  statsValue: {
    fontFamily,
    fontSize: 28,
    fontWeight: "900",
    color: colors.ink,
    lineHeight: 30,
  },
});
